/** UI languages; content the app writes for the user (demo data, main groups) comes in each. */
export const LANGUAGES = ["en", "de"] as const;
export type Language = (typeof LANGUAGES)[number];

/**
 * Where a part comes from: the vehicle maker's own (genuine), the maker's original supplier in its
 * own box (OE), or another maker (aftermarket). A part can also leave it unset.
 */
export const PART_ORIGINS = ["genuine", "oe", "aftermarket"] as const;
export type PartOrigin = (typeof PART_ORIGINS)[number];

/**
 * What one of a part's numbers is (ADR 0010): all of them name the same item. `superseded` only
 * when a source says the number was replaced; another number is otherwise `alternative`.
 */
export const PART_NUMBER_KINDS = ["primary", "alternative", "superseded", "aftermarket"] as const;
export type PartNumberKind = (typeof PART_NUMBER_KINDS)[number];

/**
 * The assigned ISO 3166-1 alpha-2 country codes. Written out rather than asked of `Intl`, which
 * also knows regions that aren't countries ("EU", "QO", "ZZ").
 */
const COUNTRY_CODES = new Set(
  (
    "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR " +
    "BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ " +
    "EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW " +
    "GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY " +
    "KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV " +
    "MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY " +
    "QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG " +
    "TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
  ).split(" "),
);

/** A country a price was collected for: an ISO 3166-1 alpha-2 code in capitals ("DE", "AT"). */
export function isCountryCode(value: unknown): value is string {
  return typeof value === "string" && COUNTRY_CODES.has(value);
}

const LANGUAGE_NAMES = new Intl.DisplayNames(["en"], { type: "language", fallback: "none" });

/**
 * The language a text was collected in: an ISO 639-1 code in lower case ("de", "en", "fr"). Any
 * language, not only the app's own (`LANGUAGES`).
 */
export function isLanguageCode(value: unknown): value is string {
  return typeof value === "string" && /^[a-z]{2}$/.test(value) && !!LANGUAGE_NAMES.of(value);
}
